import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getCurrentUser } from '../../lib/auth';
import VideoReel from './VideoReel';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';

interface Video {
  id: string;
  url: string;
  poster?: string;
  user: {
    id: string;
    name: string;
    avatar: string;
    username?: string;
  };
  description: string;
  likes: number;
  comments: number;
  shares: number;
  category: string;
  language: string;
  is_paid: boolean;
  is_promoted: boolean;
  price: number;
}

interface VideoFeedProps {
  userId?: string;
}

const VideoFeed: React.FC<VideoFeedProps> = ({ userId }) => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [swipeDirection, setSwipeDirection] = useState<'up' | 'down' | null>(null);
  const navigationRef = useRef<HTMLDivElement>(null);
  const user = getCurrentUser();

  const minSwipeDistance = 50;
  const maxVisibleDots = 15;

  useEffect(() => {
    loadVideos();

    // Subscribe to video changes
    const subscription = supabase
      .channel('video_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'ponavideos' 
      }, () => {
        loadVideos();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [userId]);

  const loadVideos = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('ponavideos')
        .select(`
          id,
          video_url,
          thumbnail_url,
          description,
          user_id,
          category,
          language,
          is_visible,
          is_paid,
          is_promoted,
          shares_count,
          created_at,
          price,
          user:ponousers!user_id(
            id,
            email,
            username,
            profile_image
          )
        `)
        .eq('is_visible', true);

      // Add user ID filter if provided
      if (userId) {
        query = query.eq('user_id', userId);
      }

      const { data: videoData, error: videoError } = await query
        .order('created_at', { ascending: false });

      if (videoError) throw videoError;
      if (!videoData) throw new Error('No video data received');

      const formattedVideos = videoData.map((video: any) => ({
        id: video.id,
        url: video.video_url,
        poster: video.thumbnail_url,
        user: {
          id: video.user?.id,
          name: video.user?.username || video.user?.email?.split('@')[0] || 'Anonymous',
          username: video.user?.username,
          avatar:
            video.user?.profile_image ||
            `https://api.dicebear.com/7.x/initials/svg?seed=${video.user?.email}`
        },
        description: video.description,
        likes: 0,
        comments: 0,
        shares: video.shares_count || 0,
        category: video.category,
        language: video.language,
        is_paid: video.is_paid,
        is_promoted: video.is_promoted,
        price: video.price || 0
      }));

      setVideos(formattedVideos);
    } catch (err) {
      console.error('Error loading videos:', err);
      setError(err instanceof Error ? err.message : 'Failed to load videos');
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientY);
    setSwipeDirection(null);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;
    
    const currentTouch = e.touches[0].clientY;
    setTouchEnd(currentTouch);
    
    // Calculate direction for visual feedback
    if (touchStart - currentTouch > 0) {
      setSwipeDirection('up');
    } else {
      setSwipeDirection('down');
    }
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isSwipe = Math.abs(distance) > minSwipeDistance;

    if (isSwipe) {
      if (distance > 0 && currentIndex < videos.length - 1) {
        // Swipe up - next video
        setCurrentIndex(prev => prev + 1);
      } else if (distance < 0 && currentIndex > 0) {
        // Swipe down - previous video
        setCurrentIndex(prev => prev - 1);
      }
    }

    setTouchStart(null);
    setTouchEnd(null);
    setSwipeDirection(null);
  };

  const handleNext = () => {
    if (currentIndex < videos.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  // Calculate which dots to show
  const getVisibleDots = () => {
    if (videos.length <= maxVisibleDots) return videos;

    const halfVisible = Math.floor(maxVisibleDots / 2);
    let start = Math.max(0, currentIndex - halfVisible);
    let end = Math.min(videos.length, start + maxVisibleDots);

    // Adjust start if we're near the end
    if (end === videos.length) {
      start = Math.max(0, end - maxVisibleDots);
    }

    return videos.slice(start, end);
  };

  if (loading && videos.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (error && videos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-black text-white">
        <p className="text-xl mb-4">{error}</p>
        <button
          onClick={loadVideos}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  const currentVideo = videos[currentIndex];

  if (!currentVideo) {
    return null;
  }

  return (
    <div 
      className="h-screen bg-black"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Left Side Navigation Area */}
      <div
        ref={navigationRef}
        className="absolute left-0 top-0 bottom-0 w-12 z-50 flex flex-col items-center justify-center"
      >
        <motion.div
          className="bg-black bg-opacity-0 rounded-lg p-2 space-y-4"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className={`p-1 rounded-full ${
              currentIndex === 0
                ? 'text-gray-500 cursor-not-allowed'
                : 'text-white hover:bg-white hover:bg-opacity-20'
            } transition-colors`}
          >
            <FaChevronUp className="w-4 h-4" />
          </button>

          <div className="space-y-1 max-h-[60vh] overflow-hidden">
            {getVisibleDots().map((_, index) => (
              <div
                key={index}
                className={`w-1.5 h-1.5 rounded-full mx-auto transition-all duration-200 ${
                  index === currentIndex
                    ? 'bg-white scale-125'
                    : 'bg-gray-500 scale-100'
                }`}
              />
            ))}
          </div>

          <button
            onClick={handleNext}
            disabled={currentIndex === videos.length - 1}
            className={`p-1 rounded-full ${
              currentIndex === videos.length - 1
                ? 'text-gray-500 cursor-not-allowed'
                : 'text-white hover:bg-white hover:bg-opacity-20'
            } transition-colors`}
          >
            <FaChevronDown className="w-4 h-4" />
          </button>
        </motion.div>
      </div>

      {/* Swipe Direction Indicator */}
      <AnimatePresence>
        {swipeDirection && (
          <motion.div
            className={`fixed inset-0 pointer-events-none z-40 flex items-center justify-center`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className={`text-white text-opacity-20 text-6xl transform ${
              swipeDirection === 'up' ? '-translate-y-12' : 'translate-y-12'
            } transition-transform`}>
              {swipeDirection === 'up' ? '↑' : '↓'}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentVideo.id}
          className="h-full"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          transition={{ duration: 0.3 }}
        >
          <VideoReel
            video={currentVideo}
            onLike={() => {}}
            onComment={() => {}}
            isActive={true}
            isOwner={user?.id === currentVideo.user.id}
          />
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default VideoFeed;